import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import axios from 'axios';
import './App.css';
import { initScrollAnimations } from './utils/scrollAnimations';

// Components
import Header from './components/Header';
import Footer from './components/Footer';
import HomePage from './components/HomePage';
import ProductPage from './components/ProductPage';
import CategoryPage from './components/CategoryPage';
import CollectionPage from './components/CollectionPage';
import StealsDealsPage from './components/StealsDealsPage';
import CheckoutSinglePage from './components/CheckoutSinglePage';
import CheckoutSuccess from './components/CheckoutSuccess';
import ContactPage from './components/ContactPage';
import AboutPage from './components/AboutPage';
import AdminLogin from './components/admin/AdminLogin';
import AdminDashboard from './components/admin/AdminDashboard';
import { Toaster } from './components/ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState(() => {
    // Initialize cart from localStorage if available
    try {
      const savedCart = localStorage.getItem('sneaker_cart');
      return savedCart ? JSON.parse(savedCart) : [];
    } catch (error) {
      console.error('Error loading cart from localStorage:', error);
      return [];
    }
  });
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('sneaker_cart', JSON.stringify(cart));
    } catch (error) {
      console.error('Error saving cart to localStorage:', error);
    }
  }, [cart]);

  // Initialize sample data and check admin login on first load
  useEffect(() => {
    const initializeData = async () => {
      try {
        await axios.post(`${API}/init-sample-data`);
        await fetchProducts();
      } catch (error) {
        console.error('Error initializing data:', error);
        setLoading(false);
      }
    };

    // Check if admin is logged in
    const adminLoggedIn = localStorage.getItem('kicks_admin_logged_in') === 'true';
    setIsAdminLoggedIn(adminLoggedIn);

    initializeData();
    
    // Initialize scroll animations
    initScrollAnimations();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/products?limit=1000`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const addToCart = (product, variant, quantity = 1) => {
    const maxStock = variant.stock_quantity;
    
    const cartItem = {
      id: `${product.id}-${variant.id}`,
      productId: product.id,
      variantId: variant.id,
      name: product.name,
      brand: product.brand,
      price: product.price,
      size: variant.size,
      color: variant.color,
      image: product.image_urls[0],
      quantity: quantity,
      maxStock: maxStock
    };

    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.id === cartItem.id);
      if (existingItem) {
        const newQuantity = existingItem.quantity + quantity;
        
        // Check if new quantity exceeds stock
        if (newQuantity > maxStock) {
          // Import toast dynamically or use alert
          const remaining = maxStock - existingItem.quantity;
          if (remaining > 0) {
            alert(`Only ${remaining} more item(s) available in stock. Total: ${maxStock} available.`);
            return prevCart.map(item =>
              item.id === cartItem.id
                ? { ...item, quantity: maxStock }
                : item
            );
          } else {
            alert(`Maximum stock of ${maxStock} already in cart.`);
            return prevCart;
          }
        }
        
        return prevCart.map(item =>
          item.id === cartItem.id
            ? { ...item, quantity: newQuantity, maxStock: maxStock }
            : item
        );
      } else {
        // Check if initial quantity exceeds stock
        if (quantity > maxStock) {
          alert(`Only ${maxStock} item(s) available in stock.`);
          return [...prevCart, { ...cartItem, quantity: maxStock }];
        }
        return [...prevCart, cartItem];
      }
    });
  };

  const updateCartItemQuantity = (itemId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(itemId);
      return;
    }
    
    setCart(prevCart =>
      prevCart.map(item => {
        if (item.id === itemId) {
          // Check against stock limit
          if (newQuantity > item.maxStock) {
            alert(`Maximum stock available: ${item.maxStock}`);
            return { ...item, quantity: item.maxStock };
          }
          return { ...item, quantity: newQuantity };
        }
        return item;
      })
    );
  };

  const removeFromCart = (itemId) => {
    setCart(prevCart => prevCart.filter(item => item.id !== itemId));
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getCartItemCount = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  const clearCart = () => {
    setCart([]);
    try {
      localStorage.removeItem('sneaker_cart');
    } catch (error) {
      console.error('Error clearing cart from localStorage:', error);
    }
  };

  return (
    <div className="App">
      <Router>
        <Routes>
          {/* Admin Routes - Exact paths, highest priority */}
          <Route 
            path="/admin" 
            element={
              <AdminLogin 
                onLogin={setIsAdminLoggedIn}
              />
            } 
          />
          <Route 
            path="/admin/dashboard/*" 
            element={
              <AdminDashboard 
                isLoggedIn={isAdminLoggedIn}
                onLogout={setIsAdminLoggedIn}
              />
            } 
          />

          {/* Public Homepage */}
          <Route 
            path="/" 
            element={
              <>
                <Header 
                  cart={cart} 
                  cartItemCount={getCartItemCount()}
                  cartTotal={getCartTotal()}
                  updateCartItemQuantity={updateCartItemQuantity}
                  removeFromCart={removeFromCart}
                />
                <main className="min-h-screen">
                  <HomePage 
                    products={products}
                    loading={loading}
                    addToCart={addToCart}
                  />
                </main>
                <Footer />
              </>
            } 
          />

          {/* Other Public Routes */}
          <Route 
            path="/collection" 
            element={
              <>
                <Header 
                  cart={cart} 
                  cartItemCount={getCartItemCount()}
                  cartTotal={getCartTotal()}
                  updateCartItemQuantity={updateCartItemQuantity}
                  removeFromCart={removeFromCart}
                />
                <main className="min-h-screen">
                  <CollectionPage addToCart={addToCart} />
                </main>
                <Footer />
              </>
            } 
          />
          <Route 
            path="/steals-deals" 
            element={
              <>
                <Header 
                  cart={cart} 
                  cartItemCount={getCartItemCount()}
                  cartTotal={getCartTotal()}
                  updateCartItemQuantity={updateCartItemQuantity}
                  removeFromCart={removeFromCart}
                />
                <main className="min-h-screen">
                  <StealsDealsPage addToCart={addToCart} />
                </main>
                <Footer />
              </>
            } 
          />
          <Route 
            path="/contact" 
            element={
              <>
                <Header 
                  cart={cart} 
                  cartItemCount={getCartItemCount()}
                  cartTotal={getCartTotal()}
                  updateCartItemQuantity={updateCartItemQuantity}
                  removeFromCart={removeFromCart}
                />
                <main className="min-h-screen">
                  <ContactPage />
                </main>
                <Footer />
              </>
            } 
          />
          <Route 
            path="/about" 
            element={
              <>
                <Header 
                  cart={cart} 
                  cartItemCount={getCartItemCount()}
                  cartTotal={getCartTotal()}
                  updateCartItemQuantity={updateCartItemQuantity}
                  removeFromCart={removeFromCart}
                />
                <main className="min-h-screen">
                  <AboutPage />
                </main>
                <Footer />
              </>
            } 
          />
          <Route 
            path="/product/:id" 
            element={
              <>
                <Header 
                  cart={cart} 
                  cartItemCount={getCartItemCount()}
                  cartTotal={getCartTotal()}
                  updateCartItemQuantity={updateCartItemQuantity}
                  removeFromCart={removeFromCart}
                />
                <main className="min-h-screen">
                  <ProductPage addToCart={addToCart} />
                </main>
                <Footer />
              </>
            } 
          />
          <Route 
            path="/category/:category" 
            element={
              <>
                <Header 
                  cart={cart} 
                  cartItemCount={getCartItemCount()}
                  cartTotal={getCartTotal()}
                  updateCartItemQuantity={updateCartItemQuantity}
                  removeFromCart={removeFromCart}
                />
                <main className="min-h-screen">
                  <CategoryPage addToCart={addToCart} />
                </main>
                <Footer />
              </>
            } 
          />
          <Route 
            path="/checkout" 
            element={
              <>
                <Header 
                  cart={cart} 
                  cartItemCount={getCartItemCount()}
                  cartTotal={getCartTotal()}
                  updateCartItemQuantity={updateCartItemQuantity}
                  removeFromCart={removeFromCart}
                />
                <main className="min-h-screen">
                  <CheckoutSinglePage 
                    cart={cart} 
                    cartTotal={getCartTotal()}
                    getCartItemCount={getCartItemCount}
                    clearCart={clearCart}
                  />
                </main>
                <Footer />
              </>
            } 
          />
          <Route 
            path="/checkout/success" 
            element={
              <>
                <Header 
                  cart={cart} 
                  cartItemCount={getCartItemCount()}
                  cartTotal={getCartTotal()}
                  updateCartItemQuantity={updateCartItemQuantity}
                  removeFromCart={removeFromCart}
                />
                <main className="min-h-screen">
                  <CheckoutSuccess />
                </main>
                <Footer />
              </>
            } 
          />
        </Routes>
        <Toaster />
      </Router>
    </div>
  );
}

export default App;